import { Injectable, OnDestroy, Inject } from '@angular/core';

import { Observable, SubscriptionLike, Subject, Observer, interval } from 'rxjs';
import { filter, map, share, distinctUntilChanged, takeWhile } from 'rxjs/operators';

import { WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';

//import { IWebsocketService, IWsMessage } from './websocket.interfaces';
// import { config } from './websocket.config';


@Injectable({
  providedIn: 'root',
})
export class SocketService implements OnDestroy {

  private config: WebSocketSubjectConfig<WebSocketMessage<any>>;

  private _websocketSub: SubscriptionLike;
  private _statusSub: SubscriptionLike;
  private _reconnection$: Observable<number>;
  private _websocket$: WebSocketSubject<WebSocketMessage<any>>;
  private _connection$: Observer<boolean>;
  private _wsMessages$: Subject<WebSocketMessage<any>>;
  private _reconnectInterval: number;
  private _reconnectAttempts: number;
  private _isConnected: boolean;
  private _status$: Observable<boolean>;

  constructor() {
    this._wsMessages$ = new Subject<WebSocketMessage<any>>();

    const protocol = window.location.protocol.match(/^https/) ? 'wss' : 'ws';
    const url = `${protocol}://${window.location.host}/ws`;

    const wsConfig: any = {
      url,
    };

    this._reconnectInterval = wsConfig.reconnectInterval || 5000;
    this._reconnectAttempts = wsConfig.reconnectAttempts || 10;

    this.config = {
      url: wsConfig.url,
      closeObserver: {
        next: (event: CloseEvent) => {
          this._websocket$ = null;
          this._connection$.next(false);
        },
      },
      openObserver: {
        next: (event: Event) => {
          //console.log('WebSocket connected!');
          this._connection$.next(true);
        },
      },
    };

    // connection status
    this._status$ = new Observable<boolean>((observer) => {
      this._connection$ = observer;
    }).pipe(share(), distinctUntilChanged());

    // run reconnect if not connection
    this._statusSub = this._status$
      .subscribe((isConnected) => {
        this._isConnected = isConnected;

        if (!this._reconnection$ && typeof(isConnected) === 'boolean' && !isConnected) {
          this._reconnect();
        }
      });

    this._websocketSub = this._wsMessages$
      .subscribe({
        error: (error: ErrorEvent) => console.error('WebSocket error!', error),
      });

    this._connect();
  }

  public on<T>(event: string): Observable<T> {
    if (event) {
      return this._wsMessages$
        .pipe(
          filter((message: WebSocketMessage<T>) => message.event === event),
          map((message: WebSocketMessage<T>) => message.data),
        );
    }
  }

  public send(event: string, data: any = {}): void {
    if (event && this._isConnected) {
      this._websocket$.next({ event, data });
    } else {
      console.error('Send error!');
    }
  }

  public ngOnDestroy(): void {
    this._websocketSub.unsubscribe();
    this._statusSub.unsubscribe();
  }

  private _connect(): void {
    this._websocket$ = new WebSocketSubject(this.config);

    this._websocket$
      .subscribe({
        next: (message) => this._wsMessages$.next(message),
        error: (error: Event) => {
          if (!this._websocket$) {
            this._reconnect();
          }
        },
      });
  }


  private _reconnect(): void {
    this._reconnection$ = interval(this._reconnectInterval)
      .pipe(takeWhile((v, index) => index < this._reconnectAttempts && !this._websocket$));

    this._reconnection$
      .subscribe({
        next: () => {
          this._connect();
        },
        complete:
          () => {
            // Subject complete if reconnect attemts ending
            this._reconnection$ = null;

            if (!this._websocket$) {
              this._wsMessages$.complete();
              this._connection$.complete();
            }
          },
      });
  }

}

export interface WebSocketConfig {
  url: string;
  reconnectInterval?: number;
  reconnectAttempts?: number;
}

export interface WebSocketMessage<T> {
  event: string;
  data: T;
}
